import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { IMAGES } from "../data/content";
import { Motif } from "../components/Motifs";
import { Reveal } from "../components/Primitives";

const METHOD = [
  { name: "LONDON", motif: "crown", color: "#C4A2E8", desc: "Legacy, identity, and purpose" },
  { name: "LOFT", motif: "loft", color: "#A8D8F0", desc: "Vision, elevation, and new perspectives" },
  { name: "LYFE", motif: "leaf", color: "#4FD6AD", desc: "Intentional living and sustainable well-being" },
  { name: "LOTUS", motif: "lotus", color: "#E59BC9", desc: "Healing, growth, and transformation" },
  { name: "LIGHTHOUSE", motif: "lighthouse", color: "#D6B45A", desc: "Guidance, leadership, and illumination" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 grain">
        <div className="container-loft grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* LEFT — editorial content */}
          <div className="order-2 lg:order-1">
            <Reveal>
              <p className="font-serif italic text-3xl md:text-4xl text-gold">About</p>
              <h1 className="font-serif font-semibold leading-[0.92] tracking-tight mt-1">
                <span className="block text-5xl md:text-7xl text-gradient">SHANTEL</span>
                <span className="block text-5xl md:text-7xl text-[#4FD6AD]">LONDON</span>
              </h1>
              <div className="flex items-center gap-3 mt-5">
                <span className="h-px w-16 bg-[#D6B45A]/60" />
                <span className="text-[#C4A2E8]">✦</span>
                <span className="h-px w-16 bg-[#D6B45A]/60" />
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 text-[#EFE7FA]/85 font-light leading-relaxed text-base md:text-lg">
                <p>Hello, I'm Shantel London, founder of London's Loft of Lyfe™ and creator of The 5L Method™.</p>
                <p>
                  I am an educator, consultant, advocate, and strategist with more than two decades of experience
                  serving children, families, educators, and communities. Throughout my journey, I have learned that
                  meaningful change happens when people are given the tools, guidance, and support to heal, grow, and
                  build lasting legacies.
                </p>
                <p>
                  At London's Loft of Lyfe™, I combine my passion for education, wellness, business strategy, family
                  advocacy, and AI-powered solutions to help individuals and organizations move from overwhelm to
                  intentional action.
                </p>
              </div>
            </Reveal>

            {/* 5L METHOD list */}
            <Reveal delay={0.15}>
              <p className="overline mt-12 mb-6">The 5L Method™ is the heart of my work</p>
              <div className="space-y-4">
                {METHOD.map((m) => (
                  <div key={m.name} data-testid={`about-5l-${m.name.toLowerCase()}`} className="flex items-center gap-5">
                    <div
                      className="h-14 w-14 shrink-0 rounded-full grid place-items-center"
                      style={{
                        border: `1px solid ${m.color}66`,
                        background: `radial-gradient(circle at center, ${m.color}26, transparent 70%)`,
                        boxShadow: `0 0 22px ${m.color}33`,
                      }}
                    >
                      <Motif name={m.motif} className="w-7 h-7" stroke={m.color} strokeWidth={1.2} />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl tracking-wide" style={{ color: m.color }}>{m.name}</h3>
                      <p className="text-sm text-[#EFE7FA]/70 font-light">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Mission */}
            <Reveal delay={0.2}>
              <div className="mt-12 card-soft p-7 md:p-8">
                <p className="font-serif italic text-2xl text-gold mb-3">My mission is simple:</p>
                <p className="text-[#EFE7FA]/85 font-light leading-relaxed">
                  to empower families, educators, and entrepreneurs through care, strategy, and sustainable solutions
                  that create meaningful impact and generational success.
                </p>
              </div>
            </Reveal>

            {/* Belief quote */}
            <Reveal delay={0.25}>
              <div className="mt-6 card-soft p-7 md:p-8 relative">
                <Quote className="text-[#D6B45A] mb-3" size={26} />
                <p className="font-serif text-xl md:text-2xl font-light leading-relaxed text-[#EFE7FA]">
                  I believe every person has the capacity to bloom like the{" "}
                  <span className="text-[#E59BC9]">Lotus</span>, be guided by the{" "}
                  <span className="text-[#D6B45A]">Lighthouse</span>, rise into the vision of the{" "}
                  <span className="text-[#A8D8F0]">Loft</span>, honor their{" "}
                  <span className="text-[#C4A2E8]">Legacy</span>, and live a purpose-filled{" "}
                  <span className="text-[#4FD6AD]">Lyfe</span>.
                </p>
              </div>
            </Reveal>

            {/* Welcome */}
            <Reveal delay={0.3}>
              <div className="mt-10">
                <p className="font-serif italic text-4xl md:text-5xl text-gold">Welcome</p>
                <p className="mt-2 text-[#EFE7FA]/80 font-light text-lg">
                  to London's Loft of Lyfe™ — where care, strategy, and growth meet.
                </p>
                <Link to="/contact" data-testid="about-cta" className="btn-primary mt-7">
                  Work With Shantel <ArrowRight size={18} />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — emblem + portrait */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-28">
            <Reveal>
              <div className="flex justify-center lg:justify-end mb-6">
                <div className="relative grain">
                  <div className="glow-ice absolute inset-0 scale-125" />
                  <img src={IMAGES.emblem} alt="London's Loft of Lyfe emblem" className="relative z-10 w-32 md:w-40 object-contain drop-shadow-[0_0_25px_rgba(214,180,90,0.4)]" />
                </div>
              </div>
              <div className="relative grain rounded-[2rem] overflow-hidden border border-[#D6B45A]/25 shadow-[0_30px_90px_rgba(91,44,131,0.5)]">
                <div className="glow-ice absolute inset-0" />
                <img src={IMAGES.founder} alt="Shantel London" className="relative z-10 w-full object-cover" />
              </div>
              <div className="mt-5 text-center">
                <p className="font-serif text-2xl text-[#EFE7FA]">Shantel London</p>
                <p className="overline mt-1">Educator · Consultant · Advocate · Strategist</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
